import {
  useDebitCreditInfo,
  useFeeInfo,
  useTransactionType,
  useWalletInfo,
} from 'views/hooks/useTransactionsDetails';
import { IDepositExtended } from 'views/types/typeTransaction';
import {
  AMLSection,
  ClientInfoFields,
  FeeBreakdownSection,
  TransactionHashSection,
} from './CommonFieldsProps';
import { formatDate } from 'utils/date';
import { statusToText } from 'utils/status-to-text';
import { EOrderType } from 'views/types/transactions';
import Decimal from 'decimal.js';
import { EngineRun } from '../EnginePath';

const DepositWithdrawDetails: React.FC<{
  transaction: IDepositExtended;
  isInternal: boolean;
}> = ({ transaction, isInternal }) => {
  const transactionType = useTransactionType(transaction);
  const debitCreditInfo = useDebitCreditInfo(transaction);
  const walletInfo = useWalletInfo(transaction, isInternal);
  const feeInfo = useFeeInfo(transaction);
  const isAml = [EOrderType.AML_TX_CHECK, EOrderType.AML_WALLET_CHECK].includes(
    transaction?.type as EOrderType
  );

  const totalFee = feeInfo.reduce(
    (acc, fee) => acc + parseFloat(fee.amount),
    0
  );

  return (
    <ul className="flex flex-col gap-3">
      <li>
        Type:{' '}
        <span className="font-semibold ml-3 capitalize">{transactionType}</span>
      </li>

      <li>
        Currency:{' '}
        <span className="font-semibold ml-3">{debitCreditInfo.currency}</span>
      </li>

      {!isAml && (
        <li>
          Network:{' '}
          <span className="font-semibold ml-3">
            {isInternal ? 'Internal' : debitCreditInfo.network}
          </span>
        </li>
      )}

      <ClientInfoFields
        transaction={transaction}
        isInternal={isInternal}
        hideNetwork={isAml}
      />

      {!isAml && (
        <li>
          {debitCreditInfo.type}:{' '}
          <span className="font-semibold ml-3">
            {debitCreditInfo.amount} {debitCreditInfo.currency}
          </span>
        </li>
      )}

      {isAml ? null : isInternal ? (
        <>
          <li>
            From User:{' '}
            <span className="font-semibold ml-3">{walletInfo.from}</span>
          </li>
          <li>
            To User: <span className="font-semibold ml-3">{walletInfo.to}</span>
          </li>
        </>
      ) : (
        <>
          <li>
            From Wallet:{' '}
            <span className="font-semibold ml-3">{walletInfo.from}</span>
          </li>
          <li>
            To Wallet:{' '}
            <span className="font-semibold ml-3">{walletInfo.to}</span>
          </li>
        </>
      )}

      {!isAml && !isInternal && (
        <li className="flex flex-col">
          <div>
            Amount:{' '}
            <span className="font-semibold ml-3">
              {Decimal.max(
                new Decimal(debitCreditInfo.amount).minus(totalFee),
                0
              ).toNumber()}{' '}
              {debitCreditInfo.currency}
            </span>
          </div>
          <FeeBreakdownSection feeInfo={feeInfo} />
        </li>
      )}

      <li>
        Date:{' '}
        <span className="font-semibold ml-3">
          {transaction?.createdAt || transaction?.created_at
            ? formatDate(
                transaction?.createdAt || transaction?.created_at,
                'DD.MM.YYYY HH:mm'
              )
            : '---'}
        </span>
      </li>

      {transaction?.completedOrFailedAt && (
        <li>
          Status Changed at:{' '}
          <span className="font-semibold ml-3">
            {formatDate(transaction.completedOrFailedAt, 'DD.MM.YYYY HH:mm')}
          </span>
        </li>
      )}

      {isAml ? (
        <li>
          AML score:{' '}
          <span className="font-semibold ml-3">
            {transaction?.amlLog?.responseData?.risk_score !== null &&
            transaction?.amlLog?.responseData?.risk_score !== undefined
              ? `${Math.round(transaction?.amlLog?.responseData?.risk_score * 100)}%`
              : '---'}
          </span>
        </li>
      ) : (
        <AMLSection transaction={transaction} isInternal={isInternal} />
      )}

      <li>
        Transaction status:{' '}
        <span className="font-semibold ml-3 capitalize">
          {statusToText(transaction?.status, transaction?.type)}
        </span>
        {['failed'].includes(transaction?.status?.toLowerCase?.()) &&
          transaction?.reasonOfFailure && (
            <div className="mt-2">
              Reason:{' '}
              <span className="font-semibold">
                {transaction.reasonOfFailure}
              </span>
            </div>
          )}
      </li>

      <li>
        Order: <span className="font-semibold ml-3">{transaction?.id}</span>
      </li>

      {!isAml && (
        <TransactionHashSection
          transaction={transaction}
          isInternal={isInternal}
        />
      )}

      {transaction.engineRun && (
        <>
          <li>
            Engine Run: <EngineRun engineRun={transaction.engineRun} />
          </li>
          <li>
            Engine Run Status:{' '}
            <span className="font-semibold ml-3">
              {transaction.engineRun.status}
            </span>
          </li>
        </>
      )}
    </ul>
  );
};
export default DepositWithdrawDetails;
